import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import KpiCard from "@/components/KpiCard";
import LoadFactorChart from "@/components/LoadFactorChart";
import { MonthlyKwhChart, Co2TrendChart, CostBreakdownChart } from "@/components/MonthlyCharts";
import AIInsights from "@/components/AIInsights";
import BreakdownSection from "@/components/BreakdownSection";
import PerformanceKPIs from "@/components/PerformanceKPIs";
import YoYCharts from "@/components/YoYCharts";
import { adminMe, adminSummary, adminUsers } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";

export default function Admin() {
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [data, setData] = useState(null);
  const [usersData, setUsersData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("avg"); // "avg" | "total"
  const [insightsKey, setInsightsKey] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminMe();
        setIsAdmin(!!r.is_admin);
      } catch (e) {
        setIsAdmin(false);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, u] = await Promise.all([adminSummary(), adminUsers()]);
      setData(d);
      setUsersData(u);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F4]">
        <div className="font-mono-data text-sm text-[#6E716C]">Verifying admin…</div>
      </div>
    );
  }
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  const months = data ? (mode === "avg" ? data.avg_months : data.total_months) : [];
  const kpis = data ? (mode === "avg" ? data.avg_kpis : data.total_kpis) : {};
  const hasData = months.length > 0;
  const meta = data?.meta || {};

  return (
    <Layout>
      <div data-testid={ADMIN.page} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="mb-10"
        >
          <div className="overline mb-3">Admin · Aggregate view</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
            The nation, in kilowatt-hours.
          </h1>
          <p className="mt-4 text-base text-[#6E716C] max-w-xl font-body">
            Aggregate across all Kilowatt Ledger users. Sample data excluded — real bills only.
          </p>

          <div className="mt-8 flex items-center gap-6 flex-wrap">
            <span data-testid={ADMIN.usersCount} className="font-mono-data text-sm">
              <span className="text-[#6E716C]">Users:</span>{" "}
              <span className="text-[#0F3524] font-semibold">{meta.total_users || 0}</span>
            </span>
            <span data-testid={ADMIN.billsCount} className="font-mono-data text-sm">
              <span className="text-[#6E716C]">Bills:</span>{" "}
              <span className="text-[#0F3524] font-semibold">{meta.total_bills || 0}</span>
            </span>
          </div>
        </motion.div>

        <div
          data-testid={ADMIN.modeToggle}
          className="mb-8 inline-flex border border-[#E5E5DE] bg-white"
        >
          <button
            data-testid={ADMIN.modeAvg}
            onClick={() => setMode("avg")}
            className={`px-5 py-2.5 text-xs font-mono-data uppercase tracking-widest transition-colors ${
              mode === "avg" ? "bg-[#0F3524] text-[#F7F7F4]" : "text-[#6E716C] hover:text-[#0F3524]"
            }`}
          >
            Averages
          </button>
          <button
            data-testid={ADMIN.modeTotal}
            onClick={() => setMode("total")}
            className={`px-5 py-2.5 text-xs font-mono-data uppercase tracking-widest transition-colors ${
              mode === "total" ? "bg-[#0F3524] text-[#F7F7F4]" : "text-[#6E716C] hover:text-[#0F3524]"
            }`}
          >
            Totals
          </button>
        </div>

        {loading ? (
          <div className="font-mono-data text-sm text-[#6E716C]">Loading aggregate ledger…</div>
        ) : !hasData ? (
          <div className="flat-card p-10 sm:p-16 text-center">
            <div className="overline mb-4">No real bills yet</div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight max-w-xl mx-auto">
              The archive is waiting for its first real entry.
            </h2>
            <p className="text-[#6E716C] mt-4 max-w-lg mx-auto">
              Sample seeded rows are excluded from admin analytics. Once users upload real bills, they&apos;ll appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard
                testid={ADMIN.kpiCard("kwh")}
                label={mode === "avg" ? "Avg kWh (all months)" : "Total kWh"}
                value={fmt(kpis.total_kwh, 0)}
                unit="kWh"
                delta={`${signed(kpis.delta_kwh_pct)}% MoM`}
                deltaDir={kpis.delta_kwh_pct > 0 ? "up" : kpis.delta_kwh_pct < 0 ? "down" : ""}
                index={0}
              />
              <KpiCard
                testid={ADMIN.kpiCard("cost")}
                label={mode === "avg" ? "Avg cost (all months)" : "Total cost"}
                value={`RM ${fmt(kpis.total_cost_rm, 2)}`}
                delta={`${signed(kpis.delta_cost_pct)}% MoM`}
                deltaDir={kpis.delta_cost_pct > 0 ? "up" : kpis.delta_cost_pct < 0 ? "down" : ""}
                index={1}
              />
              <KpiCard
                testid={ADMIN.kpiCard("cpk")}
                label="Avg cost / kWh"
                value={`RM ${fmt(kpis.avg_cost_per_kwh, 4)}`}
                delta={`${signed(kpis.delta_cpk_pct)}% MoM`}
                deltaDir={kpis.delta_cpk_pct > 0 ? "up" : kpis.delta_cpk_pct < 0 ? "down" : ""}
                index={2}
              />
              <KpiCard
                testid={ADMIN.kpiCard("lf")}
                label="Avg load factor"
                value={fmt(kpis.avg_load_factor, 3)}
                delta={`CO₂ ${fmt(kpis.total_co2_kg, 0)} kg`}
                deltaDir=""
                index={3}
              />
            </div>

            <LoadFactorChart months={months} spikes={[]} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <MonthlyKwhChart months={months} />
              <Co2TrendChart months={months} />
              <CostBreakdownChart months={months} />
            </div>

            {/* Performance & KPIs — national averages */}
            <PerformanceKPIs months={months} kpis={kpis} />

            {/* Year-over-Year 4-chart grid — always shows trend */}
            <YoYCharts series={null} yoy={{ months_tracked: months.length }} months={months} />

            <AIInsights
              hasBills={hasData}
              refreshKey={insightsKey}
              endpointPath="/api/admin/insights/stream"
              overline="Admin · Aggregate narrator"
              title="Malaysian households, on average"
              subtitle="Claude Sonnet 4.5 reads all Kilowatt Ledger users&apos; bills and reports the national trend."
              testid={ADMIN.aiInsights}
              refreshTestid={ADMIN.refreshInsightsBtn}
              emptyText="Once real bills are uploaded, the aggregate narrator will speak."
            />

            <div className="flat-card overflow-hidden">
              <div className="p-6 sm:p-8 border-b border-[#E5E5DE]">
                <div className="overline mb-2">Ledger · Aggregate</div>
                <h3 className="font-display text-xl tracking-tight">
                  Month-by-month ({mode === "avg" ? "averages" : "totals"})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#F7F7F4]">
                      {["Month", "Bills", "Users", "kWh", "RM", "RM/kWh", "Load factor", "CO₂ kg"].map((h) => (
                        <th
                          key={h}
                          className="text-left font-mono-data uppercase tracking-widest text-[10px] text-[#6E716C] px-4 sm:px-6 py-3 whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {months.map((m, i) => (
                      <tr key={i} className="border-b border-[#E5E5DE] hover:bg-[#FAFAF7] transition-colors">
                        <td className="px-4 sm:px-6 py-4 font-display text-base tracking-tight">{m.month_label}</td>
                        <td className="px-4 sm:px-6 py-4 font-mono-data text-sm">{m.n_bills}</td>
                        <td className="px-4 sm:px-6 py-4 font-mono-data text-sm">{m.n_users}</td>
                        <td className="px-4 sm:px-6 py-4 font-mono-data text-sm">{m.kwh.toFixed(0)}</td>
                        <td className="px-4 sm:px-6 py-4 font-mono-data text-sm">{m.cost_rm.toFixed(2)}</td>
                        <td className="px-4 sm:px-6 py-4 font-mono-data text-sm">{m.cost_per_kwh.toFixed(4)}</td>
                        <td className="px-4 sm:px-6 py-4 font-mono-data text-sm">{m.load_factor.toFixed(3)}</td>
                        <td className="px-4 sm:px-6 py-4 font-mono-data text-sm">{m.co2_kg.toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* State & district breakdowns (every KPI) */}
            <BreakdownSection
              mode={mode}
              state_kpis={data?.state_kpis || []}
              district_kpis={data?.district_kpis || []}
            />

            {/* Users section — count, state breakdown, table */}
            <UsersSection users={usersData} />
          </div>
        )}
      </div>
    </Layout>
  );
}

function UsersSection({ users }) {
  if (!users) return null;
  const { total_users = 0, state_breakdown = [], users: rows = [] } = users;
  const maxCount = Math.max(1, ...state_breakdown.map((s) => s.count));

  return (
    <div data-testid={ADMIN.usersSection} className="space-y-4">
      <div className="flat-card p-6 sm:p-8">
        <div className="flex justify-between items-baseline flex-wrap gap-2 mb-6">
          <div>
            <div className="overline mb-2">People · Currently using Kilowatt Ledger</div>
            <h3 className="font-display text-2xl tracking-tight">
              {total_users} {total_users === 1 ? "household" : "households"} on file.
            </h3>
            <p className="text-sm text-[#6E716C] mt-1">Real bills only. Admin accounts excluded.</p>
          </div>
        </div>

        {state_breakdown.length > 0 && (
          <div data-testid={ADMIN.stateBreakdown} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 pt-2">
            {state_breakdown.map((s) => (
              <div
                key={s.state}
                data-testid={ADMIN.stateRow(s.state)}
                className="grid grid-cols-[110px_1fr_40px] items-center gap-3"
              >
                <span className="font-mono-data text-xs text-[#6E716C] uppercase tracking-widest truncate">{s.state}</span>
                <div className="h-3 bg-[#F0F0EA] relative">
                  <div
                    className="absolute inset-y-0 left-0 bg-[#0F3524]"
                    style={{ width: `${(s.count / maxCount) * 100}%` }}
                  />
                </div>
                <span className="font-mono-data text-sm text-right text-[#0F3524]">{s.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flat-card overflow-hidden" data-testid={ADMIN.usersTable}>
        <div className="p-6 sm:p-8 border-b border-[#E5E5DE]">
          <div className="overline mb-2">Directory</div>
          <h3 className="font-display text-xl tracking-tight">Every user, every household.</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#F7F7F4]">
                {["User", "Email", "TNB account", "City", "District", "State", "Bills", "Latest"].map((h) => (
                  <th
                    key={h}
                    className="text-left font-mono-data uppercase tracking-widest text-[10px] text-[#6E716C] px-4 sm:px-6 py-3 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr
                  key={u.user_id}
                  data-testid={ADMIN.userRow(u.user_id)}
                  className="border-b border-[#E5E5DE] hover:bg-[#FAFAF7] transition-colors"
                >
                  <td className="px-4 sm:px-6 py-4 font-display text-base tracking-tight">{u.name}</td>
                  <td className="px-4 sm:px-6 py-4 font-mono-data text-xs text-[#6E716C]">{u.email}</td>
                  <td className="px-4 sm:px-6 py-4 font-mono-data text-xs">{u.account_number || "—"}</td>
                  <td className="px-4 sm:px-6 py-4 font-mono-data text-xs">{u.city}</td>
                  <td className="px-4 sm:px-6 py-4 font-mono-data text-xs">{u.district}</td>
                  <td className="px-4 sm:px-6 py-4 font-mono-data text-xs">{u.state}</td>
                  <td className="px-4 sm:px-6 py-4 font-mono-data text-sm">{u.bill_count}</td>
                  <td className="px-4 sm:px-6 py-4 font-mono-data text-xs text-[#6E716C]">{u.latest_bill_month || "—"}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center font-mono-data text-sm text-[#6E716C]">
                    No non-admin users with real bills yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function fmt(n, digits = 2) {
  if (typeof n !== "number" || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-MY", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
function signed(n) {
  if (typeof n !== "number") return "0.00";
  return n > 0 ? `+${n.toFixed(2)}` : n.toFixed(2);
}
